import {observer} from "mobx-react-lite";
import {Frame} from "../components/common/Frame";
import {Header} from "../components/sections/Header";
import {ReactNode, useContext} from "react";
import {SectionTitle} from "../components/common/SectionTitle";
import styled from "@emotion/styled";
import {D} from "../Constants";
import {Typography} from "@mui/material";
import {useNavigate} from "react-router";
import {AppRoutes} from "../AppRoutes";
import {MyRecordingsStoreContext, OrganizationRecordingsStoreContext} from "../stores/RecordingsStore";
import {LessonsStoreContext} from "../stores/LessonsStore";
import {renderRecordingCards} from "../components/pages/sessions/Sessions";
import {renderLessonCards} from "./Lessons";

export const HomePage = observer(() => {
  const myRecordingsStore = useContext(MyRecordingsStoreContext)!;
  const organizationRecordingsStore = useContext(OrganizationRecordingsStoreContext)!;
  const lessonsStore = useContext(LessonsStoreContext)!;
  const navigate = useNavigate();

  return (
    <Frame>
      <Header/>
      <Frame contentWidth paddingV={2} gap={2}>
        <SectionTitle onClick={() => {
          navigate(AppRoutes.mySessions);
        }}
        >My sessions ({myRecordingsStore.recordings?.pagination?.total ?? '...'})</SectionTitle>
        <GridContainer>
          {renderRecordingCards(
            myRecordingsStore?.recordings?.recordings ? (myRecordingsStore?.recordings?.recordings || []).slice(0, 4) : undefined,
            (recording) => {
              if (!recording.id) {
                return;
              }
              navigate(AppRoutes.mySessionPath(recording.id));
            }
          )}
          {!myRecordingsStore.loading && myRecordingsStore.recordings?.recordings?.length === 0 && (
            <EmptyState>Nothing recorded yet.</EmptyState>
          )}
        </GridContainer>

        <SectionTitle onClick={() => {
          navigate(AppRoutes.organizationSessions);
        }}
        >Organization sessions ({organizationRecordingsStore.recordings?.pagination?.total ?? '...'})</SectionTitle>
        <GridContainer>
          {renderRecordingCards(
            organizationRecordingsStore?.recordings?.recordings ? (organizationRecordingsStore?.recordings?.recordings || []).slice(0, 4) : undefined,
            (recording) => {
              if (!recording.id) {
                return;
              }
              navigate(AppRoutes.organizationSessionPath(recording.id));
            }
          )}
          {!organizationRecordingsStore.loading && organizationRecordingsStore.recordings?.recordings?.length === 0 && (
            <EmptyState>Your organisation has no shared sessions yet.</EmptyState>
          )}
        </GridContainer>

        <SectionTitle onClick={() => {
          navigate(AppRoutes.lessons);
        }}
        >Lessons ({lessonsStore.lessons?.pagination?.total ?? '...'})</SectionTitle>
        {renderLessonCards(
          lessonsStore.lessons?.lessons ? (lessonsStore.lessons?.lessons || []).slice(0, 4) : undefined,
          (lesson) => {
            if (!lesson.id) {
              return;
            }
            navigate(AppRoutes.lessonPath(lesson.id));
          }
        )}
        {!lessonsStore.loading && lessonsStore.lessons?.lessons?.length === 0 && (
          <EmptyState>No lessons available.</EmptyState>
        )}
      </Frame>
    </Frame>
  );
});

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    grid-template-rows: 1fr 0 0 0 0 0;
    overflow: hidden;
    column-gap: ${D.unit(2)};

    @media (max-width: 600px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const EmptyState = ({children}: { children: ReactNode }) => (
  <Typography variant="body2" color="textSecondary" style={{gridColumn: "1 / -1", padding: D.unit(2)}}>
    {children}
  </Typography>
);
